/** @jsxImportSource @emotion/react */
import { Close, Search } from '@mui/icons-material'
import { Box, Input, LinearProgress, useTheme } from '@mui/material'
import { useDebounce } from '@app/hooks/useDebounce'
import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom' // ← NEW

import { useFetchPropertySuggestions } from '../hooks/useFetchPropertySuggestions'
import { useHandlePropertySearch } from '../hooks/useHandlePropertySearch'
import { PropertySuggestion } from '../types'
import { suggestionHighlightText } from '../utils/suggestionHighlightText'
import {
  closeButton,
  propertySearchbarContainer,
  searchIcon,
  suggestionsColumn,
  suggestionsDropdown,
  suggestionsPlaceholder,
} from './PropertySearch.styles'
import { useKeyboardNavigation } from '../hooks/usePropertyKeyboardNavigation'
import { propertyConfigAtom } from '@app/state/propertyAtoms'

const MIN_SEARCH_LENGTH = 3
const QUERY_KEY = 'query'

type PropertySearchbarProps = {
  onSuggestionSelected?: (suggestion: PropertySuggestion | null) => void
  searchOnClickSuggestion?: boolean
  size?: 'small' | 'medium'
}

export const PropertySearch = ({
  onSuggestionSelected,
  size = 'medium',
  searchOnClickSuggestion = false,
}: PropertySearchbarProps) => {
  const theme = useTheme()

  const [searchParams, setSearchParams] = useSearchParams()
  const initialQuery = useRef(searchParams.get(QUERY_KEY))
  const [inputValue, setInputValue] = useState('')
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [isSuggestionsHovered, setIsSuggestionsHovered] = useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = useState<PropertySuggestion | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<Array<HTMLDivElement | null>>([])

  const debouncedSearch = useDebounce(inputValue, 300)

  const { data: propertyConfig } = useAtomValue(propertyConfigAtom)
  const isSearchValid = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH
  const showSuggestions = (isInputFocused || isSuggestionsHovered) && debouncedSearch.length > 0
  const showClearButton = inputValue.length > 0
  const shouldFetch = isSearchValid && showSuggestions && !selectedSuggestion

  // Fetch suggestions via custom hook/service
  const { data: suggestions = [], isLoading } = useFetchPropertySuggestions(
    propertyConfig?.asset_type,
    debouncedSearch,
    shouldFetch,
  )

  useEffect(() => {
    if (initialQuery.current) {
      setInputValue(initialQuery.current)
      setIsInputFocused(true)
    }
  }, [])

  // Reset highlighted index when suggestions change
  useEffect(() => {
    suggestionsRef.current = new Array(suggestions.length).fill(null)
    setHighlightedIndex(suggestions.length > 0 ? 0 : -1)
  }, [suggestions.length])

  // Notify parent on selection change
  useEffect(() => {
    if (onSuggestionSelected) {
      onSuggestionSelected(selectedSuggestion)
    }
  }, [selectedSuggestion, onSuggestionSelected])

  // UPDATE URL ONLY when suggestion is selected
  useEffect(() => {
    if (selectedSuggestion) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(QUERY_KEY, selectedSuggestion.search_match)
          return next
        },
        { replace: true },
      )
    }
  }, [selectedSuggestion, setSearchParams])

  const onHandleSearchProperty = useHandlePropertySearch(selectedSuggestion)

  const handleInputChange = (v: string) => {
    setInputValue(v)
    if (selectedSuggestion) setSelectedSuggestion(null)
  }

  const handleClear = () => {
    setInputValue('')
    setSelectedSuggestion(null)
    setHighlightedIndex(-1)
    // Immediate URL clear
    const next = new URLSearchParams(searchParams)
    next.delete(QUERY_KEY)
    setSearchParams(next, { replace: true })
  }

  const handleSuggestionClick = (suggestion: PropertySuggestion) => {
    setInputValue(suggestion.search_match)
    setSelectedSuggestion(suggestion)
    setIsSuggestionsHovered(false)
    setHighlightedIndex(-1)
    inputRef.current?.blur()

    if (searchOnClickSuggestion) {
      onHandleSearchProperty(suggestion)
    }
  }

  const handleKeyDown = useKeyboardNavigation({
    showSuggestions,
    suggestions,
    highlightedIndex,
    setHighlightedIndex,
    setSelectedSuggestion,
    setIsInputFocused,
    handleSuggestionClick,
  })

  const activeId = highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined
  return (
    <Box css={propertySearchbarContainer(showSuggestions, size)}>
      <Input
        inputProps={{
          role: 'combobox',
          'aria-expanded': showSuggestions,
          'aria-controls': 'property-suggestions',
          'aria-autocomplete': 'list',
          'aria-activedescendant': activeId,
          'aria-describedby': suggestions.length ? 'suggestions-count' : undefined,
        }}
        type="text"
        inputRef={inputRef}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsInputFocused(true)}
        onBlur={() => setIsInputFocused(false)}
        onKeyDown={handleKeyDown}
        placeholder="Search"
        startAdornment={<Search css={searchIcon(theme)} />}
        endAdornment={
          <>
            <Box
              sx={{
                display: 'flex',
                position: 'absolute',
                left: '0',
                bottom: '0',
                width: '100%',
              }}
            >
              <LinearProgress
                sx={{
                  width: '100%',
                  height: isLoading && showSuggestions ? '2px' : '0px',
                  transition: 'height 0.2s',
                  borderRadius: '0 0 4px 4px',
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.grey[500],
                  '& .MuiLinearProgress-bar': {
                    animationDuration: isLoading ? '1.6s' : '0s',
                    backgroundColor: theme.palette.grey[200],
                  },
                }}
              />
            </Box>
            {showClearButton && <Close css={closeButton(theme, true)} onClick={handleClear} />}
          </>
        }
        disableUnderline
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '4px',
          padding: size === 'small' ? '8px' : '12px',
          color: theme.palette.grey[600],
          gap: '8px',
          fontSize: '14px',
          transition: 'border 0.2s',
          border: selectedSuggestion
            ? `2px solid ${theme.palette.primary.main}`
            : `2px solid ${theme.palette.background.paper}`,
        }}
        autoComplete="off"
      />

      <Box
        css={suggestionsDropdown({ theme, showSuggestions, size })}
        onMouseEnter={() => setIsSuggestionsHovered(true)}
        onMouseLeave={() => setIsSuggestionsHovered(false)}
      >
        <Box
          css={suggestionsColumn({ theme, showSuggestions, loading: isLoading })}
          id="property-suggestions"
          role="listbox"
          aria-busy={isLoading}
        >
          {suggestions.length === 0 && (
            <>
              {!isSearchValid && (
                <Box css={suggestionsPlaceholder({ theme })}>
                  Type more characters to see suggestions
                </Box>
              )}

              {isSearchValid && isLoading && (
                <Box css={suggestionsPlaceholder({ theme })}>Loading...</Box>
              )}

              {isSearchValid && !isLoading && (
                <Box css={suggestionsPlaceholder({ theme })}>No matches found</Box>
              )}
            </>
          )}

          {suggestions.length > 0 &&
            suggestions.map((suggestion, idx) => (
              <Box
                key={suggestion.asset_id}
                id={`option-${idx}`}
                role="option"
                aria-selected={highlightedIndex === idx}
                ref={(el: HTMLDivElement | null) => (suggestionsRef.current[idx] = el)}
                sx={{
                  padding: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  backgroundColor:
                    highlightedIndex === idx ? theme.palette.uintel[500] + '20' : undefined,
                  '&:hover': { backgroundColor: theme.palette.uintel[500] + '20' },
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {suggestionHighlightText(suggestion.search_match, debouncedSearch)}
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  )
}
